// ============================================================================
// NPSDClientScreen.tsx - Main NPSD Client (ClientGroup) Screen Component
// Converted from Vue 2 Vue-NPSDClient.ts
//
// Orchestrating container screen that composes all client sub-components.
// Manages edit/save/update/cancel lifecycle, record locking, address validation,
// admin vs non-admin field access, notification transformations,
// site initialization, connection search modals, and case report integration.
// ============================================================================

import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useNPSDEntity } from '@/hooks/useNPSDEntity';
import { useFormSchemas } from '@/hooks/useFormSchemas';
import { FormTabPanel } from '@/components/forms/FormTabPanel';
import { NPSDTabBar } from '@/components/npsd/NPSDTabBar';
import { NPSDWarningsDisplay } from '@/components/npsd/NPSDWarningsDisplay';
import { NPSDClientDetails } from '@/components/npsd/NPSDClientDetails';
import { NPSDClientConnections } from '@/components/npsd/NPSDClientConnections';
import { NPSDClientHistory } from '@/components/npsd/NPSDClientHistory';
import { ClientSitesGrid } from '@/components/ClientSitesGrid';
import { ClientNotificationsGrid } from '@/components/ClientNotificationsGrid';
import { NPSDAttachments } from '@/components/npsd/NPSDAttachments';
import { NPSDWarnings } from '@/components/npsd/NPSDWarnings';
import { NPSDAuditLog } from '@/components/npsd/NPSDAuditLog';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SearchModal } from '@/components/modals/SearchModal';
import { NPSDPersonScreen } from '@/components/npsd/NPSDPersonScreen';
import { NPSDVehicleScreen } from '@/components/npsd/NPSDVehicleScreen';
import { NPSDBusinessScreen } from '@/components/npsd/NPSDBusinessScreen';
import { PageModal } from '@/components/modals/PageModal';
import {
  refreshPage,
  refreshWithParams,
  navigateToRecord,
  navigateToMapByAddress,
  navigateToCaseReport,
} from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { validateAddressWithSuggestions } from '@/utils/custom-location';
import moment from 'moment-timezone';
import { hasValue, hasStringValue, currentUserHasPermission, getModuleType } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';

// ============================================================================
// External Declarations
// ============================================================================

let currentClientGroup = (window as any).currentClientGroup;
let isClientGroupAdmin = (window as any).isClientGroupAdmin;
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
import { RelType } from '@/models/other-entities';
import { isInArray } from '@/utils/arrays';
import { getAvailableSubmodules } from '@/utils/auth';

const getInsecureUUID = _wg('getInsecureUUID');
const setNPSDItemLocked = _wg('setNPSDItemLocked');
const isBasePageCaseReport = _wg('isBasePageCaseReport');
const isBasePageCallCardOrCaseReport = _wg('isBasePageCallCardOrCaseReport');
const updateNPSDItemForReport = _wg('updateNPSDItemForReport');
const isNpsdDataEqual = _wg('isNpsdDataEqual');
function convertTimeToMilliseconds(time: string): number {
  const m = moment(time);
  return m.milliseconds() + 1000 * (m.seconds() + 60 * (m.minutes() + 60 * m.hours()));
}

// ============================================================================
// Module-level State
// ============================================================================

let originalNPSDClientResult: any = null;
let originalBackendNotifications: any[] | null = null;

// ============================================================================
// Notification Transformation Functions
// ============================================================================

function convertNotificationHourToMillis(hour: number): number {
  const minsInDay = 60 * 24;
  let mins = hour * 60;
  mins = (mins + minsInDay) % minsInDay;
  return 1000 * 60 * mins;
}

function transformNotificationsForForm(automatedNotificationList: any[]): any[] {
  const result: any[] = [];
  for (let i = 0; i < automatedNotificationList.length; i++) {
    const elt = automatedNotificationList[i];
    const notification: any = {
      id: elt.id,
      notifiedName: elt.notifiedName,
      notifiedEmail: elt.notifiedEmail,
      activityReportStartTimeLocal: elt.activityReportStartTimeLocal,
      activityReportEndTimeLocal: elt.activityReportEndTimeLocal,
      notificationTimeZoneId: elt.notificationTimeZoneId,
      notificationTimeLocal: elt.notificationTimeLocal,
    };

    // Transform frequency
    if (!hasValue(elt.activityReportNotificationFrequency)) {
      notification.activityReportNotificationFrequency = { code: null, value: 'None' };
    } else if (elt.activityReportNotificationFrequency === 'd') {
      notification.activityReportNotificationFrequency = { code: 'd', value: 'Daily' };
    } else if (elt.activityReportNotificationFrequency === 'w') {
      notification.activityReportNotificationFrequency = { code: 'w', value: 'Weekly' };
    } else if (elt.activityReportNotificationFrequency === 'm') {
      notification.activityReportNotificationFrequency = { code: 'm', value: 'Monthly' };
    } else {
      notification.activityReportNotificationFrequency = { code: null, value: 'None' };
    }

    // Transform boolean options
    notification.emailActivityReportWhenNoData = elt.emailActivityReportWhenNoData
      ? { code: 'true', value: 'Always Email' }
      : { code: 'false', value: 'Email Only When Data' };

    notification.receiveReportApprovedNotification = elt.receiveReportApprovedNotification
      ? { code: 'true', value: 'When Approved' }
      : { code: 'false', value: 'Never' };

    if (elt.receiveCallCardClosedNotification && elt.includeCallCardDetailsInEmail) {
      notification.receiveCallCardClosedNotification = { code: 'closed-detailed', value: 'When Closed (Detailed Email)' };
    } else if (elt.receiveCallCardClosedNotification) {
      notification.receiveCallCardClosedNotification = { code: 'closed', value: 'When Closed' };
    } else {
      notification.receiveCallCardClosedNotification = { code: 'false', value: 'Never' };
    }

    result.push(notification);
  }
  return result;
}

function convertTimeFieldToMilliseconds(value: any): number {
  if (typeof value === 'number') return value;
  // Handle Date objects and date strings (e.g. ISO "2026-03-05T13:00:00.000Z")
  if (value instanceof Date || typeof value === 'string') {
    return convertTimeToMilliseconds(moment(value).format('YYYY-MM-DD HH:mm'));
  }
  return 0;
}

function transformNotificationsForBackend(notifications: any[]): any[] {
  return notifications.map((item) => {
    const startTimeLocal = convertTimeFieldToMilliseconds(item.activityReportStartTimeLocal);
    const endTimeLocal = convertTimeFieldToMilliseconds(item.activityReportEndTimeLocal);
    const nTimeLocal = convertTimeFieldToMilliseconds(item.notificationTimeLocal);

    return {
      id: item.id,
      notifiedName: item.notifiedName,
      notifiedEmail: item.notifiedEmail,
      notificationTimeZoneId: item.notificationTimeZoneId,
      notificationTime: 0,
      notificationTimeLocal: nTimeLocal,
      activityReportNotificationFrequency: item.activityReportNotificationFrequency ? item.activityReportNotificationFrequency.code : null,
      emailActivityReportWhenNoData: item.emailActivityReportWhenNoData ? (item.emailActivityReportWhenNoData.code === 'true') : false,
      receiveReportApprovedNotification: item.receiveReportApprovedNotification ? (item.receiveReportApprovedNotification.code === 'true') : false,
      receiveCallCardClosedNotification: item.receiveCallCardClosedNotification ? (item.receiveCallCardClosedNotification.code === 'closed' || item.receiveCallCardClosedNotification.code === 'closed-detailed') : false,
      includeCallCardDetailsInEmail: item.receiveCallCardClosedNotification ? (item.receiveCallCardClosedNotification.code === 'closed-detailed') : false,
      activityReportStartTime: 0,
      activityReportEndTime: 0,
      activityReportStartTimeLocal: startTimeLocal,
      activityReportEndTimeLocal: endTimeLocal,
    };
  });
}

// ============================================================================
// Initialization Helper Functions
// ============================================================================

function initializeClientSites(client: any) {
  if (!client.sites) client.sites = [];
  for (let i = 0; i < client.sites.length; i++) {
    if (!client.sites[i].uuid) {
      client.sites[i].uuid = getInsecureUUID();
    }
  }
}

/**
 * Non-blocking coordinate attachment for client sites (Client Location Trust §4.A.2).
 *
 * Site location strings are free text by design and have never been address-validated. On save we
 * quietly geocode any site whose location was added or changed, and — only when the result is
 * trustworthy — attach latitude/longitude and an addressVerificationStatus so the coordinate can be
 * used at call time:
 *   - HIGH-confidence geocode with no user interaction → status HIGH.
 *   - The dispatcher adjusted the pin in the preview (drag / alternate / drop) → status MANUAL.
 *   - The user cancelled the preview, or the address didn't resolve → save the site as-is with no
 *     coordinate. The pin is an offer, always skippable; the site keeps its string verbatim.
 *
 * The whole step is best-effort: any failure leaves the site untouched so a geocoding problem never
 * blocks the client save.
 */
async function attachSiteCoordinates(sites: any[], originalSites: any[] | undefined, companyId: number | undefined): Promise<any[]> {
  if (!Array.isArray(sites) || sites.length === 0) return sites;
  const originalById = new Map<any, any>();
  for (const os of (originalSites ?? [])) {
    if (os && hasValue(os.id)) originalById.set(os.id, os);
  }

  const out: any[] = [];
  for (const site of sites) {
    const location = (site?.location ?? '').trim();
    const original = hasValue(site?.id) ? originalById.get(site.id) : undefined;
    const unchanged = original && (original.location ?? '') === (site?.location ?? '');
    // Skip when there's no address OR the text is unchanged — regardless of whether coords
    // exist. The pin offer fires once, when the address is new or edited; re-offering on every
    // save nagged users who deliberately skipped it (their un-geocodable site re-prompted on
    // each unrelated client edit). Skipped sites stay untrusted and are picked up by the
    // Address Review no-trusted queue or call-time write-back instead.
    if (!location || unchanged) {
      out.push(site);
      continue;
    }
    try {
      const results: any = await validateAddressWithSuggestions({
        address: location,
        isLocationRequired: false,
        // No requireConfirm: HIGH-confidence results resolve silently; only low/no-result offers
        // the preview, which the user may cancel.
        companyId,
      });
      const tmp: any = {};
      const fill = (window as any).fillInAddressFromSuggestions;
      const fields = (window as any).DataObjectLocationFields?.clientSite ?? (window as any).DataObjectLocationFields?.site;
      if (fill && fields) {
        fill(results, tmp, null, fields, false);
      }
      const next = { ...site };
      if (hasValue(tmp.latitude) && hasValue(tmp.longitude)) {
        next.latitude = tmp.latitude;
        next.longitude = tmp.longitude;
        if (hasValue(tmp.addressVerificationStatus)) next.addressVerificationStatus = tmp.addressVerificationStatus;
      }
      // Keep the site's typed location string verbatim — do not overwrite with a geocoded address.
      next.location = site.location;
      out.push(next);
    } catch {
      // Cancelled preview or geocode failure — save the site with no coordinate (non-blocking).
      out.push(site);
    }
  }
  return out;
}

function initializeClientNotifications(client: any) {
  if (!client.automatedNotificationList) client.automatedNotificationList = [];

  const list = client.automatedNotificationList;
  const transformedList = transformNotificationsForForm(list);

  for (let i = 0; i < transformedList.length; i++) {
    const item: any = transformedList[i];
    if (!item.uuid) item.uuid = getInsecureUUID();
  }

  client.automatedNotificationList = transformedList;
}

function updateGlobalCurrentClientGroup() {
  (window as any).currentClientGroup = currentClientGroup;
  App.originalCGResult = JSON.parse(JSON.stringify(currentClientGroup));
}

// ============================================================================
// Props Interface
// ============================================================================

interface NPSDClientScreenProps {
  clientData: any;
  isEditMode?: boolean;
  isSaveMode?: boolean;
  isCaseReportMode?: boolean;
  onClientSaved?: (result: any) => void;
  onClientUpdated?: (result: any) => void;
  onAddToReport?: (client: any) => void;
  onAddedToConnection?: (client: any) => void;
}

// ============================================================================
// Component
// ============================================================================

export function NPSDClientScreen({
  clientData,
  isEditMode: isEditModeProp = false,
  isSaveMode: isSaveModeProp = false,
  isCaseReportMode: isCaseReportModeProp = false,
  onClientSaved,
  onClientUpdated,
  onAddToReport,
  onAddedToConnection,
}: NPSDClientScreenProps) {
  const { theme } = useTheme();

  // ── Core State ──────────────────────────────────────────────────────
  const [client, setClient] = useState<any>(clientData);
  const [activeTab, setActiveTab] = useState('Client');
  const [isEditMode, setIsEditMode] = useState(isEditModeProp);
  const [isSaveMode, setIsSaveMode] = useState(isSaveModeProp);
  const [isCaseReportMode] = useState(isCaseReportModeProp);
  const [isMobile, setIsMobile] = useState(App?.isMobile() || false);

  // ── Option Types ────────────────────────────────────────────────────
  const [phoneOptionTypes] = useState<any[]>(() => { const v = (window as any).PhoneOptionTypes; return typeof v !== 'undefined' ? v : []; });
  const [emailOptionTypes] = useState<any[]>(() => { const v = (window as any).EmailOptionTypes; return typeof v !== 'undefined' ? v : []; });

  // ── Restricted Editing Modal ────────────────────────────────────────
  const [showRestrictedModal, setShowRestrictedModal] = useState(false);

  // ── useNPSDEntity hook ──────────────────────────────────────────────
  const {
    showConnectionSearchModal, connectionSearchType, openConnectionSearch, closeConnectionSearchModal,
    showViewPersonModal, viewPersonId, setShowViewPersonModal,
    showViewVehicleModal, viewVehicleId, setShowViewVehicleModal,
    showViewBusinessModal, viewBusinessId, setShowViewBusinessModal,
    openViewPersonModal, openViewVehicleModal, openViewBusinessModal,
    handleConnectionView,
    validateAndFillAddress,
    ensureConnectionsNewPopulated,
    handleAttachmentUpload,
  } = useNPSDEntity({
    entityType: 'client',
    setEntity: setClient,
    connectionTypes: ['people', 'vehicles', 'businesses', 'clients'],
  });

  // ── Role Dialog for Person Connections ──────────────────────────────
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [pendingConnectionItem, setPendingConnectionItem] = useState<any>(null);
  const [connectionRole, setConnectionRole] = useState('');

  // ── Refs ─────────────────────────────────────────────────────────────
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const clientRef = useRef<any>(client);

  // Keep refs in sync
  useEffect(() => { clientRef.current = client; }, [client]);

  // Sync client state when clientData prop changes
  useEffect(() => {
    setClient(clientData);
  }, [clientData]);

  // Keep global currentClientGroup in sync
  useEffect(() => {
    if (client) {
      currentClientGroup = client;
      (window as any).currentClientGroup = client;
    }
  }, [client]);

  // ── Computed Values ─────────────────────────────────────────────────

  const canEdit = useMemo(() => {
    return currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_UPDATE);
  }, []);

  const canSave = useMemo(() => {
    return currentUserHasPermission(AccessAuthorities.ROLE_NPSD_DATA_SAVE);
  }, []);

  // Form tabs have their own write permission (ROLE_FORM_ENTRY — one grant covering
  // save/update/delete), independent of Client Management. Anyone who can view the
  // client sees the tabs; only Form Entry holders can edit them, in any module.
  const canEditForms = useMemo(() => {
    return currentUserHasPermission(AccessAuthorities.ROLE_FORM_ENTRY);
  }, []);

  const showEditButton = useMemo(() => {
    return hasValue(client?.id) && !isEditMode && canEdit;
  }, [client?.id, isEditMode, canEdit]);

  const showUpdateButton = useMemo(() => {
    return hasValue(client?.id) && isEditMode && canEdit;
  }, [client?.id, isEditMode, canEdit]);

  const showCancelButton = showUpdateButton;

  const showSaveButton = useMemo(() => {
    return !hasValue(client?.id) && !isCaseReportMode && canSave;
  }, [client?.id, isCaseReportMode, canSave]);

  const showCaseReportSaveButton = useMemo(() => {
    return !hasValue(client?.id) && isCaseReportMode && canSave;
  }, [client?.id, isCaseReportMode, canSave]);

  const isReadOnly = useMemo(() => {
    return !isEditMode && !isSaveMode;
  }, [isEditMode, isSaveMode]);

  const isAdminFieldsReadOnly = useMemo(() => {
    return !isClientGroupAdmin || isReadOnly;
  }, [isReadOnly]);

  const locationExists = useMemo(() => {
    return hasValue(client?.locFormattedAddress) && client.locFormattedAddress !== '';
  }, [client?.locFormattedAddress]);

  const isCadOrMdt = useMemo(() => {
    const moduleType: string = App?.getModuleTypeFromURL() || '';
    return moduleType === 'cad' || moduleType === 'mdt';
  }, []);

  const canAccessNotifications = useMemo(() => {
    return isInArray(getAvailableSubmodules(), AccessAuthorities.ROLE_CLIENT_MANAGEMENT);
  }, []);

  const connectionSearchTypeLabel = useMemo(() => {
    const labels: Record<string, string> = {
      'person': 'Person',
      'vehicle': 'Vehicle',
      'business': 'Business',
    };
    return labels[connectionSearchType] || '';
  }, [connectionSearchType]);

  const connectionsCount = useMemo(() => {
    if (!client?.connections) return 0;
    const c = client.connections;
    return (c.people?.length || 0) + (c.vehicles?.length || 0) + (c.businesses?.length || 0);
  }, [client?.connections]);

  // Custom Forms: pull the tenant's CLIENT-attached schemas so we can render one
  // tab per schema on active clients (decision row 7 — one instance per schema+host).
  // Only auto-load once the client row is saved (we need client.id to fetch instances).
  // 'display' scope: module-scoped read endpoint, so any user who can view the
  // client sees its form tabs — no ROLE_FORM_MANAGEMENT required.
  const { schemas: formSchemas } = useFormSchemas({
    attachTo: 'CLIENT',
    autoLoad: !!client?.id,
    scope: 'display',
  });

  // Form-tab saves piggyback on the client's Update flow. We bump this counter
  // right before exiting edit mode; each FormTabPanel watches it and
  // saves its dirty values to its own instance endpoint in parallel with the
  // client save. Cancel does NOT bump it, so cancel reverts panel values.
  const [formSaveTrigger, setFormSaveTrigger] = useState(0);

  // Each form panel reports its required-field validation errors here so the
  // client save can pre-validate everything before firing. Map<schemaId, errors[]>.
  const formValidationsRef = useRef<Map<number, string[]>>(new Map());
  const handleFormValidation = useCallback((sid: number, errors: string[]) => {
    if (errors.length > 0) {
      formValidationsRef.current.set(sid, errors);
    } else {
      formValidationsRef.current.delete(sid);
    }
  }, []);

  const tabsConfig = useMemo(() => {
    const base = [
      { id: 'Client', label: 'Client' },
      { id: 'Sites', label: 'Sites', badge: client?.sites?.length || 0 },
      { id: 'Connections', label: 'Connections', badge: connectionsCount },
      { id: 'History', label: 'History', badge: client?.connections?.reports?.length || 0 },
      { id: 'Attachments', label: 'Attachments', badge: client?.attachmentList?.length || 0 },
      { id: 'Warnings', label: 'Warnings', badge: client?.warnings?.length || 0 },
      { id: 'Notifications', label: 'Notifications', badge: client?.automatedNotificationList?.length || 0, visible: canAccessNotifications },
      { id: 'AuditLog', label: 'Audit Log' },
    ];
    // Dynamic form tabs — stable label = schema.name. Hidden until the client has an id.
    const formTabs = (formSchemas || []).map((schema) => ({
      id: `Form_${schema.id}`,
      label: schema.name,
      visible: !!client?.id,
    }));
    return [...base, ...formTabs];
  }, [connectionsCount, client?.sites?.length, client?.connections?.reports?.length, client?.attachmentList?.length, client?.warnings?.length, client?.automatedNotificationList?.length, canAccessNotifications, client?.id, formSchemas]);

  // ── Effects ─────────────────────────────────────────────────────────

  // Mobile resize listener (debounced to avoid excessive re-renders during resize)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(App?.isMobile() || false), 150);
    };
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // ── Edit / Cancel Methods ───────────────────────────────────────────

  const edit = useCallback(() => {
    setIsEditMode(true);

    if (!isClientGroupAdmin) {
      setShowRestrictedModal(true);
    }

    if (hasValue(client?.id)) {
      setNPSDItemLocked(client.id.toString(), true, 'client', () => {});
    }
  }, [client?.id]);

  const closeRestrictedModal = useCallback(() => {
    setShowRestrictedModal(false);
  }, []);

  const cancel = useCallback(() => {
    const clientId = client?.id;

    if (hasValue(clientId)) {
      setNPSDItemLocked(clientId.toString(), false, 'client', () => {
        if (!isNpsdDataEqual(originalNPSDClientResult, currentClientGroup)) {
          originalNPSDClientResult = null;
          App.originalCGResult = null;
          if (!isCaseReportMode) {
            refreshPage();
          }
        } else {
          setIsEditMode(false);
        }
      });
    } else if (!isCaseReportMode) {
      history.back();
    }
  }, [client?.id, isCaseReportMode]);

  // ── Save / Update ───────────────────────────────────────────────────

  const saveOrUpdateClient = useCallback(async () => {
    // Pre-validate Custom Form tabs. Each panel reports missing required fields
    // via handleFormValidation; aborting here prevents the client save from
    // landing in a partial-success state where the client persists but its
    // forms reject. Only applies for Form Entry holders — for everyone else the
    // form tabs are read-only, so their validation state must not block a
    // connections/warnings-only save.
    if (canEditForms && formValidationsRef.current.size > 0) {
      const messages: string[] = [];
      formValidationsRef.current.forEach((errors, sid) => {
        const s = (formSchemas || []).find((sc) => sc.id === sid);
        const name = s?.name ?? `Form ${sid}`;
        messages.push(`${name}: ${errors.join(', ')}`);
      });
      App.showPopupNotification(
        `Cannot save — fix required fields first. ${messages.join('; ')}`,
        'error',
      );
      return;
    }

    // Validate and fill address fields if address is provided (only in admin mode)
    let addressFields: Record<string, any> = {};
    if (isClientGroupAdmin) {
      const result = await validateAndFillAddress(clientRef.current?.locFormattedAddress, originalNPSDClientResult);
      if (!result) return;
      addressFields = result;
    }

    // Merge address fields into the client (state update is async, so merge directly)
    let clientToSave = { ...clientRef.current, ...addressFields };

    // Non-blocking coordinate attachment for sites (admin mode only, where sites are editable).
    // Quietly geocodes changed/new site locations; HIGH results attach coords silently, low/no-match
    // offers a skippable pin. A cancel or failure saves the site with no coordinate — never blocking.
    if (isClientGroupAdmin && Array.isArray(clientToSave.sites) && clientToSave.sites.length > 0) {
      const companyId = (window as any).getCurrentCompanyId?.() ?? null;
      clientToSave.sites = await attachSiteCoordinates(clientToSave.sites, originalNPSDClientResult?.sites, companyId);
    }

    // If not in admin mode, restore admin-only fields from original data
    if (!isClientGroupAdmin && originalNPSDClientResult) {
      const connections = clientToSave.connections;
      const connectionsNew = clientToSave.connectionsNew;
      const warnings = clientToSave.warnings;
      const attachmentList = clientToSave.attachmentList;

      // Restore client info fields (admin-only)
      clientToSave.name = originalNPSDClientResult.name;
      clientToSave.website = originalNPSDClientResult.website;
      clientToSave.accountNumber = originalNPSDClientResult.accountNumber;
      clientToSave.authorizationCode = originalNPSDClientResult.authorizationCode;
      clientToSave.notes = originalNPSDClientResult.notes;
      clientToSave.timeZone = originalNPSDClientResult.timeZone;
      clientToSave.defaultNotificationTime = originalNPSDClientResult.defaultNotificationTime;

      // Restore location fields (admin-only)
      clientToSave.locFormattedAddress = originalNPSDClientResult.locFormattedAddress;
      clientToSave.locAddress = originalNPSDClientResult.locAddress;
      clientToSave.locAddress2 = originalNPSDClientResult.locAddress2;
      clientToSave.locCity = originalNPSDClientResult.locCity;
      clientToSave.locState = originalNPSDClientResult.locState;
      clientToSave.locZip = originalNPSDClientResult.locZip;
      clientToSave.locCountry = originalNPSDClientResult.locCountry;
      clientToSave.locName = originalNPSDClientResult.locName;
      clientToSave.latitude = originalNPSDClientResult.latitude;
      clientToSave.longitude = originalNPSDClientResult.longitude;
      clientToSave.customLocationId = originalNPSDClientResult.customLocationId;

      // Restore contact info (admin-only)
      clientToSave.phones = originalNPSDClientResult.phones;
      clientToSave.emails = originalNPSDClientResult.emails;

      // Restore gate codes/Access (admin-only)
      clientToSave.codes = originalNPSDClientResult.codes;

      // Restore sites (admin-only)
      clientToSave.sites = originalNPSDClientResult.sites;

      // Restore automated notifications (admin-only) - use original backend format
      clientToSave.automatedNotificationList = JSON.parse(JSON.stringify(originalBackendNotifications || []));

      // Ensure non-admin editable fields are preserved
      clientToSave.connections = connections;
      clientToSave.connectionsNew = connectionsNew;
      clientToSave.warnings = warnings;
      clientToSave.attachmentList = attachmentList;
    }

    // Populate connectionsNew with ALL current connections
    clientToSave = ensureConnectionsNewPopulated(clientToSave);

    // Trigger any dirty form-tab panels to save in parallel with the client save.
    // Form Entry holders only — for everyone else the panels are read-only, so
    // there is nothing to save (and the backend write gate would reject it anyway).
    if (canEditForms) {
      setFormSaveTrigger((t) => t + 1);
    }
    setIsEditMode(false);

    // Transform notifications back to backend format (only for admin)
    if (isClientGroupAdmin) {
      clientToSave.automatedNotificationList = transformNotificationsForBackend(clientToSave.automatedNotificationList || []);
    }

    // Update global currentClientGroup
    currentClientGroup = clientToSave;
    (window as any).currentClientGroup = clientToSave;

    if (!hasValue(clientToSave.id)) {
      // Save new client
      api.saveNPSDItem(clientToSave, 'client', (result: any) => {
        originalNPSDClientResult = null;
        App.originalCGResult = null;
        if (!isCaseReportMode) {
          refreshWithParams(result.id);
        } else {
          onClientSaved?.(result);
        }
      });
    } else {
      // Handle attachment title updates
      if (originalNPSDClientResult && originalNPSDClientResult.attachmentList) {
        for (let i = 0; i < clientToSave.attachmentList.length; i++) {
          const item = clientToSave.attachmentList[i];
          const originalItem = originalNPSDClientResult.attachmentList.find((d: any) => d.id === item.id);
          if ((hasValue(originalItem) && item.title !== originalItem.title) || !hasValue(originalItem)) {
            api.updateAttachment(item.title, item.id, () => {});
          }
        }
      }

      api.updateNPSDItem(clientToSave, 'client', (result: any) => {
        originalNPSDClientResult = null;
        App.originalCGResult = null;
        App.pauseItemChangeCheck = true;

        // Handle case report context
        if ((window as any).CaseRpt?.currentMDTRptController?.isSearchOpen()) {
          (window as any).CaseRpt?.currentMDTRptController.closeInvolvedSearchModal();
        }

        if (isCaseReportMode) {
          onClientUpdated?.(result);
        } else if (!isBasePageCallCardOrCaseReport()) {
          refreshPage();
        }

        if (isBasePageCaseReport()) {
          updateNPSDItemForReport(RelType.client, clientToSave);
        }
      }, (error: any) => {
        setIsEditMode(true);
        App?.showPopupNotification('Failed to update client', 'error');
      });
    }
  }, [validateAndFillAddress, ensureConnectionsNewPopulated, isCaseReportMode, onClientSaved, onClientUpdated, canEditForms]);

  // ── Add to Report ───────────────────────────────────────────────────

  const addToReport = useCallback(() => {
    const addToReportFn = (window as any).NPSDItemToReport;
    if (typeof addToReportFn === 'function') addToReportFn('lnkRptAddClientGroup', client.id);
    onAddToReport?.(client);

    if ((window as any).CaseRpt?.currentMDTRptController?.isSearchOpen()) {
      (window as any).CaseRpt?.currentMDTRptController.closeInvolvedSearchModal();
    }
  }, [client, onAddToReport]);

  const addToConnection = useCallback(() => {
    onAddedToConnection?.(client);
  }, [client, onAddedToConnection]);

  // ── Gate Code Management ────────────────────────────────────────────

  const addGateCode = useCallback(() => {
    setClient((prev: any) => ({
      ...prev,
      gateCodes: [...(prev.gateCodes || []), { gateCode: '' }],
    }));
  }, []);

  const removeGateCode = useCallback((gateCode: any) => {
    setClient((prev: any) => {
      const index = prev.gateCodes?.indexOf(gateCode);
      if (index > -1) {
        const updated = [...prev.gateCodes];
        updated.splice(index, 1);
        return { ...prev, gateCodes: updated };
      }
      return prev;
    });
  }, []);

  // ── Warning Management ──────────────────────────────────────────────

  const handleWarningsChange = useCallback((updatedWarnings: any[]) => {
    setClient((prev: any) => ({ ...prev, warnings: updatedWarnings }));
  }, []);

  // ── Map View ────────────────────────────────────────────────────────

  const viewMap = useCallback(() => {
    if (!client?.locFormattedAddress) return;
    navigateToMapByAddress(client.locFormattedAddress);
  }, [client?.locFormattedAddress]);

  // ── Client Change Handler ───────────────────────────────────────────

  const handleClientChange = useCallback((updatedClient: any) => {
    setClient(updatedClient);
  }, []);

  // ── Sites Update Handler ────────────────────────────────────────────

  const handleSitesUpdate = useCallback((newSites: any[]) => {
    setClient((prev: any) => ({ ...prev, sites: newSites }));
  }, []);

  // ── Notifications Change Handler ────────────────────────────────────

  const handleNotificationsChange = useCallback((newNotifications: any[]) => {
    setClient((prev: any) => {
      const next = { ...prev, automatedNotificationList: newNotifications };
      clientRef.current = next;
      return next;
    });
  }, []);

  // ── Attachment Management ───────────────────────────────────────────

  const addAttachment = useCallback(() => {
    if (!isSaveMode && !isEditMode) {
      edit();
    }
    if (attachmentInputRef.current) {
      attachmentInputRef.current.click();
    }
  }, [isSaveMode, isEditMode, edit]);

  const confirmRemoveAttachment = useCallback((attachment: any) => {
    if (!attachment) return;

    setClient((prev: any) => ({
      ...prev,
      attachmentList: (prev.attachmentList || []).filter((a: any) => a.id !== attachment.id),
      attachmentInfoList: (prev.attachmentInfoList || []).filter((a: any) => a.id !== attachment.id),
    }));
  }, []);

  // ── Add Connection to Entity ────────────────────────────────────────

  const addConnectionToEntity = useCallback((type: string, item: any, role?: string) => {
    const pluralType = type === 'person' ? 'people' :
                       type === 'business' ? 'businesses' :
                       type + 's';

    let connection: any = { id: item.id };

    if (type === 'person') {
      connection = {
        id: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        dob: item.dob,
        roles: role || '',
      };
    } else if (type === 'vehicle') {
      connection = {
        id: item.id,
        plateNumber: item.plateNumber,
        plateState: item.plateState,
        make: item.make,
        model: item.model,
        color: item.color,
        vin: item.vin,
      };
    } else if (type === 'business') {
      connection = {
        id: item.id,
        businessName: item.businessName || item.name,
        branchName: item.branchName,
        website: item.website,
      };
    }

    setClient((prev: any) => {
      const updatedConnections = { ...prev.connections };
      const updatedConnectionsNew = { ...(prev.connectionsNew || { people: [], vehicles: [], businesses: [], clients: [], reports: [] }) };

      if (!updatedConnections[pluralType]) updatedConnections[pluralType] = [];
      if (!updatedConnectionsNew[pluralType]) updatedConnectionsNew[pluralType] = [];

      updatedConnections[pluralType] = [...updatedConnections[pluralType], connection];
      updatedConnectionsNew[pluralType] = [...updatedConnectionsNew[pluralType], connection];

      return {
        ...prev,
        connections: updatedConnections,
        connectionsNew: updatedConnectionsNew,
      };
    });

    App?.showPopupNotification('Connection added.', 'success');
    closeConnectionSearchModal();
  }, [closeConnectionSearchModal]);

  // ── Handle Connection Select ────────────────────────────────────────

  const handleConnectionSelect = useCallback((item: any) => {
    const type = connectionSearchType;
    const pluralType = type === 'person' ? 'people' :
                       type === 'business' ? 'businesses' :
                       type + 's';

    const existingArray = client?.connections?.[pluralType] || [];
    const isDuplicate = existingArray.some((c: any) => c.id === item.id);

    if (isDuplicate) {
      App?.showPopupNotification('This connection already exists.', 'warning');
      return;
    }

    // For person connections, show dialog to enter Title/Role
    if (type === 'person') {
      setPendingConnectionItem(item);
      setConnectionRole('');
      closeConnectionSearchModal();
      setShowRoleDialog(true);
      return;
    }

    // Add connection directly for non-person types
    addConnectionToEntity(type, item);
  }, [connectionSearchType, client?.connections, closeConnectionSearchModal, addConnectionToEntity]);

  // ── View Connection from Existing Connections Panel ──────────────────

  const viewNpsdItem = useCallback((type: string, item: any) => {
    const id = typeof item === 'object' ? item.id : item;
    switch (type) {
      case 'person': openViewPersonModal(id); break;
      case 'vehicle': openViewVehicleModal(id); break;
      case 'business': openViewBusinessModal(id); break;
    }
  }, [openViewPersonModal, openViewVehicleModal, openViewBusinessModal]);


  // ── Reload client data (e.g. after closing a view modal) ──────────
  const reloadClient = useCallback(() => {
    if (client?.id) {
      api.getNPSDItemById(String(client.id), 'client', (result: any) => {
        const data = new (window as any).ClientGroup(result);
        initializeClientSites(data);
        initializeClientNotifications(data);
        setClient(data);
      });
    }
  }, [client?.id]);

  // ── View Modal Close Handlers ───────────────────────────────────────

  const closeViewPersonModal = useCallback(() => {
    setShowViewPersonModal(false);
    reloadClient();
  }, [reloadClient, setShowViewPersonModal]);

  const closeViewVehicleModal = useCallback(() => {
    setShowViewVehicleModal(false);
    reloadClient();
  }, [reloadClient, setShowViewVehicleModal]);

  const closeViewBusinessModal = useCallback(() => {
    setShowViewBusinessModal(false);
    reloadClient();
  }, [reloadClient, setShowViewBusinessModal]);

  // ── Role Dialog ─────────────────────────────────────────────────────

  const confirmRoleDialog = useCallback(() => {
    if (pendingConnectionItem) {
      addConnectionToEntity('person', pendingConnectionItem, connectionRole);
    }
    setShowRoleDialog(false);
    setPendingConnectionItem(null);
    setConnectionRole('');
  }, [pendingConnectionItem, connectionRole, addConnectionToEntity]);

  const closeRoleDialog = useCallback(() => {
    setShowRoleDialog(false);
    setPendingConnectionItem(null);
    setConnectionRole('');
  }, []);

  // ── Connection Handlers for Sub-components ──────────────────────────

  const handleAddConnection = useCallback((connectionType: string) => {
    openConnectionSearch(connectionType);
  }, [openConnectionSearch]);

  const handleRemoveConnection = useCallback((pluralType: string, item: any) => {
    setClient((prev: any) => {
      const updatedConnections = { ...prev.connections };
      const updatedConnectionsNew = { ...(prev.connectionsNew || {}) };

      if (updatedConnectionsNew[pluralType]) {
        updatedConnectionsNew[pluralType] = updatedConnectionsNew[pluralType].filter((c: any) => c.id !== item.id);
      }
      if (updatedConnections[pluralType]) {
        updatedConnections[pluralType] = updatedConnections[pluralType].filter((c: any) => c.id !== item.id);
      }

      return {
        ...prev,
        connections: updatedConnections,
        connectionsNew: updatedConnectionsNew,
      };
    });
  }, []);

  // ── History Click Handler ───────────────────────────────────────────

  const handleViewReport = useCallback((reportId: number) => {
    navigateToCaseReport(reportId);
  }, []);

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <div className={`npsd-entity-screen npsd-client-screen${theme ? ' ' + theme : ''}`}>
      {/* Header Section */}
      <div className="profile-card-header">
        <div className="profile-cover-details">
          <div className="profile-info">
            <h3 className="client-title">{client?.name || 'New Client'}</h3>
          </div>
        </div>
        <div className="action-buttons">
          {showEditButton && (
            <button className="btn btn-header" onClick={edit}>Edit</button>
          )}
          {showUpdateButton && (
            <button className="kstc-btn kstc-btn--success" onClick={saveOrUpdateClient}>Update</button>
          )}
          {showCancelButton && (
            <button className="kstc-btn kstc-btn--danger" onClick={cancel}>Cancel</button>
          )}
          {showSaveButton && (
            <button className="kstc-btn kstc-btn--success" onClick={saveOrUpdateClient}>Save</button>
          )}
          {showCaseReportSaveButton && (
            <button className="kstc-btn kstc-btn--success" onClick={saveOrUpdateClient}>Save & Add</button>
          )}
        </div>
        <NPSDWarningsDisplay warnings={client?.warnings || []} />
      </div>

      {/* Tab Navigation */}
      <NPSDTabBar
        tabs={tabsConfig}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      <div className="client-tab-content">
        {/* Client Details Tab */}
        <div className="client-tab-panel" style={{ display: activeTab === 'Client' ? undefined : 'none' }}>
          <NPSDClientDetails
            client={client}
            isReadOnly={isReadOnly}
            isAdminReadOnly={isAdminFieldsReadOnly}
            phoneOptionTypes={phoneOptionTypes}
            emailOptionTypes={emailOptionTypes}
            locationExists={locationExists}
            isCadOrMdt={isCadOrMdt}
            onAddGateCode={addGateCode}
            onRemoveGateCode={removeGateCode}
            onViewMap={viewMap}
            onClientChange={handleClientChange}
          />
        </div>

        {/* Sites Tab */}
        <div className="client-tab-panel" style={{ display: activeTab === 'Sites' ? undefined : 'none' }}>
          <ClientSitesGrid
            sites={client?.sites || []}
            isReadOnly={isAdminFieldsReadOnly}
            onChange={handleSitesUpdate}
          />
        </div>

        {/* Connections Tab */}
        <div className="client-tab-panel" style={{ display: activeTab === 'Connections' ? undefined : 'none' }}>
          <NPSDClientConnections
            connections={client?.connections}
            isReadOnly={isReadOnly}
            onViewConnection={viewNpsdItem}
            onAddConnection={handleAddConnection}
            onRemoveConnection={handleRemoveConnection}
          />
        </div>

        {/* History Tab */}
        <div className="client-tab-panel" style={{ display: activeTab === 'History' ? undefined : 'none' }}>
          <NPSDClientHistory
            reports={client?.connections ? client.connections.reports : []}
            onViewReport={handleViewReport}
          />
        </div>

        {/* Attachments Tab */}
        <div className="client-tab-panel" style={{ display: activeTab === 'Attachments' ? undefined : 'none' }}>
          <NPSDAttachments
            attachmentList={client?.attachmentList || []}
            isEditMode={!isReadOnly}
            showProfilePicOption={false}
            onAdd={addAttachment}
            onRemove={confirmRemoveAttachment}
          />
          <input
            type="file"
            ref={attachmentInputRef}
            style={{ display: 'none' }}
            onChange={handleAttachmentUpload}
            multiple
          />
        </div>

        {/* Warnings Tab */}
        <div className="client-tab-panel" style={{ display: activeTab === 'Warnings' ? undefined : 'none' }}>
          <NPSDWarnings
            warnings={client?.warnings || []}
            isReadOnly={isReadOnly}
            title="Warnings"
            onChange={handleWarningsChange}
          />
        </div>

        {/* Notifications Tab */}
        <div className="client-tab-panel" style={{ display: activeTab === 'Notifications' ? undefined : 'none' }}>
          <ClientNotificationsGrid
            notifications={client?.automatedNotificationList || []}
            isReadOnly={isAdminFieldsReadOnly}
            title="Automated Notifications"
            onChange={handleNotificationsChange}
          />
        </div>

        {/* Audit Log Tab */}
        <div className="client-tab-panel" style={{ display: activeTab === 'AuditLog' ? undefined : 'none' }}>
          <NPSDAuditLog
            entityId={client?.id}
            entityType="client"
            title="Audit Log"
          />
        </div>

        {/* Custom Forms — one dynamic tab per CLIENT-attached schema (decision row 7).
            Panels stay mounted across tab switches so dirty edits in one tab survive
            navigating to another tab and clicking Update. Form data is editable for
            Form Entry holders in any module (matching the backend write gate,
            ROLE_FORM_ENTRY); read-only for everyone else. */}
        {(formSchemas || []).map((schema) => {
          const tabId = `Form_${schema.id}`;
          const isActive = activeTab === tabId;
          return (
            <div
              key={tabId}
              className="client-tab-panel"
              style={{ display: isActive ? undefined : 'none' }}
            >
              <FormTabPanel
                schemaId={schema.id}
                recordType="CLIENT"
                recordId={client?.id ?? null}
                hostNoun="client"
                isEditMode={isEditMode && canEditForms}
                saveTrigger={formSaveTrigger}
                onValidationChange={handleFormValidation}
              />
            </div>
          );
        })}
      </div>

      {/* Restricted Editing Modal */}
      <ConfirmModal
        open={showRestrictedModal}
        title="Editing Restricted"
        messageHtml="Only connections, attachments, and warnings may be edited in this module. Full editing is only enabled in the Admin module."
        confirmText="OK"
        showCancel={false}
        onConfirm={closeRestrictedModal}
        onCancel={closeRestrictedModal}
      />

      {/* Connection Search Modal */}
      <SearchModal
        open={showConnectionSearchModal}
        searchType={connectionSearchType as any}
        title={'Add ' + connectionSearchTypeLabel + ' Connection'}
        onSelect={handleConnectionSelect}
        onView={handleConnectionView}
        onCancel={closeConnectionSearchModal}
      />

      {/* View Person Modal */}
      <PageModal
        open={showViewPersonModal}
        title="View Person"
        modalId="clientViewPerson"
        onClose={closeViewPersonModal}
      >
        {showViewPersonModal && viewPersonId && (
          <NPSDPersonScreen
            personId={viewPersonId}
            isWindow={true}
            onClose={closeViewPersonModal}
          />
        )}
      </PageModal>

      {/* View Vehicle Modal */}
      <PageModal
        open={showViewVehicleModal}
        title="View Vehicle"
        modalId="clientViewVehicle"
        onClose={closeViewVehicleModal}
      >
        {showViewVehicleModal && viewVehicleId && (
          <NPSDVehicleScreen
            vehicleId={viewVehicleId}
            isWindow={true}
            onClose={closeViewVehicleModal}
          />
        )}
      </PageModal>

      {/* View Business Modal */}
      <PageModal
        open={showViewBusinessModal}
        title="View Business"
        modalId="clientViewBusiness"
        onClose={closeViewBusinessModal}
      >
        {showViewBusinessModal && viewBusinessId && (
          <NPSDBusinessScreen
            businessId={viewBusinessId}
            isModal={true}
            onClose={closeViewBusinessModal}
          />
        )}
      </PageModal>

      {/* Role Dialog for Person Connections */}
      <ConfirmModal
        open={showRoleDialog}
        title="Person Connection"
        confirmText="Add Connection"
        cancelText="Cancel"
        onConfirm={confirmRoleDialog}
        onCancel={closeRoleDialog}
      >
        <div className="role-dialog-content">
          {pendingConnectionItem && (
            <p>
              Adding connection to: <strong>{pendingConnectionItem.firstName} {pendingConnectionItem.lastName}</strong>
            </p>
          )}
          <div className="field-group">
            <label>Title/Role (optional)</label>
            <input
              type="text"
              className="kstc-input"
              value={connectionRole}
              onChange={(e) => setConnectionRole(e.target.value)}
              placeholder="e.g. Owner, Manager, Employee"
            />
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
}

// ============================================================================
// Screen Registry Wrapper
// ============================================================================

/**
 * Wrapper component that loads client data from an ID and renders
 * NPSDClientScreen once data is ready.
 */
export function NPSDClientScreenWrapper({ clientId, isAdmin, isCaseReportMode, onClientUpdated }: { clientId?: string | null; isAdmin?: boolean; isCaseReportMode?: boolean; onClientUpdated?: (result: any) => void }) {
  const [clientData, setClientData] = useState<any>(null);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      isClientGroupAdmin = true;
    }

    App.setProgressControl();

    if (clientId) {
      // Load existing client
      api.getNPSDItemById(clientId, 'client', (result: any) => {
        const data = new (window as any).ClientGroup(result);

        originalBackendNotifications = JSON.parse(JSON.stringify(data.automatedNotificationList || []));
        initializeClientSites(data);
        initializeClientNotifications(data);

        currentClientGroup = data;
        (window as any).currentClientGroup = data;
        originalNPSDClientResult = JSON.parse(JSON.stringify(data));
        updateGlobalCurrentClientGroup();

        setClientData(data);
        setLoading(false);
      });
    } else {
      // New client
      const data = new (window as any).ClientGroup('');

      if (!data.notificationDefaultTimeZoneId) {
        data.notificationDefaultTimeZoneId = moment.tz.guess();
      }
      if (data.notificationDefaultTime == null) {
        const defaultHour = 8;
        data.notificationDefaultTime = convertNotificationHourToMillis(defaultHour);
      }

      originalBackendNotifications = [];
      initializeClientSites(data);
      initializeClientNotifications(data);

      currentClientGroup = data;
      (window as any).currentClientGroup = data;
      originalNPSDClientResult = JSON.parse(JSON.stringify(data));
      updateGlobalCurrentClientGroup();

      setClientData(data);
      setIsNew(true);
      setLoading(false);
    }
  }, [clientId, isAdmin]);

  if (loading || !clientData) return null;

  return (
    <NPSDClientScreen
      clientData={clientData}
      isEditMode={isNew}
      isSaveMode={isNew}
      isCaseReportMode={isCaseReportMode}
      onClientUpdated={onClientUpdated}
    />
  );
}

// ── Screen registry ──────────────────────────────────────────────────────

